import type { ChangeEvent, FormEvent, KeyboardEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonListItemDto,
  AdminStudentsResponseDto,
  BulkCreateEnrollmentsResponseDto,
  EnrollmentDto,
  EnrollmentStatus,
  EnrollmentsResponseDto
} from "@madamuh/domain";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchAdminLessons } from "../../shared/api/adminLesson";
import {
  bulkCreateEnrollments,
  fetchAdminEnrollments,
  fetchAdminStudents
} from "../../shared/api/adminRoster";
import { adminSearchMatches, buildAdminSearchHaystack } from "../../shared/lib/adminSearch";
import { GENERIC_TENANT_LABELS, getTenantLabels, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

function buildAdminLinks(labels: TenantLabels) {
  return [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/students", label: labels.student },
  { to: "/t/:tenantSlug/teacher/admin/lessons", label: labels.lesson },
  { to: "/t/:tenantSlug/teacher/admin/enrollments", label: labels.enrollment }
];
}

type EligibilityFilter = "eligible" | "all" | "alreadyCurrentlyEnrolled" | "pastHistory";
type StudentStatusFilter = "active" | "inactive" | "all";
type SortKey = "displayNameKana" | "displayName" | "memberCode" | "familyAccount";
type SortOrder = "asc" | "desc";
type PageSize = 25 | 50 | 100;
type BulkEnrollmentStudentState = "eligible" | "alreadyCurrentlyEnrolled" | "inactive";
type CurrentEnrollmentStatus = "pendingStart" | "active" | "paused";
type CurrentEnrollmentStatusFilter = "all" | CurrentEnrollmentStatus;

type BulkStudentRow = {
  studentId: string;
  memberCode: string;
  displayName: string;
  displayNameKana: string | null;
  birthDate: string;
  status: "active" | "inactive";
  familyAccountDisplayName: string | null;
  state: BulkEnrollmentStudentState;
  searchText: string;
  currentLessons: string[];
  selectedLessonHistory: EnrollmentDto[];
  hasPastSelectedLessonHistory: boolean;
};

type CurrentEnrollmentStudentRow = {
  studentId: string;
  memberCode: string;
  displayName: string;
  displayNameKana: string | null;
  birthDate: string;
  status: "active" | "inactive";
  familyAccountDisplayName: string | null;
  searchText: string;
  currentEnrollments: EnrollmentDto[];
  primaryEnrollment: EnrollmentDto;
  hasDuplicateCurrentEnrollments: boolean;
  hasInactiveStudentWarning: boolean;
};

type BulkFormState = {
  joinDate: string;
  leaveDate: string;
  notesInternal: string;
};

const defaultFormState: BulkFormState = {
  joinDate: "",
  leaveDate: "",
  notesInternal: ""
};

const currentEnrollmentStatuses = new Set<EnrollmentStatus>(["pendingStart", "active", "paused"]);
const currentEnrollmentStatusOrder: Record<CurrentEnrollmentStatus, number> = {
  active: 0,
  pendingStart: 1,
  paused: 2
};

function parsePageSize(value: string | null): PageSize {
  return value === "50" ? 50 : value === "100" ? 100 : 25;
}

function parsePositiveInteger(value: string | null): number {
  const parsed = value ? Number.parseInt(value, 10) : 1;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function isCurrentEnrollmentStatus(status: EnrollmentStatus): status is CurrentEnrollmentStatus {
  return currentEnrollmentStatuses.has(status);
}

function resolveStudentState(student: AdminStudentsResponseDto["items"][number], selectedLessonHistory: EnrollmentDto[]): BulkEnrollmentStudentState {
  if (student.status !== "active") {
    return "inactive";
  }

  return selectedLessonHistory.some((enrollment) =>
    enrollment.status === "pendingStart" || enrollment.status === "active" || enrollment.status === "paused"
  )
    ? "alreadyCurrentlyEnrolled"
    : "eligible";
}

function stateBadgeLabel(state: BulkEnrollmentStudentState, labels: TenantLabels): string {
  switch (state) {
    case "eligible":
      return "登録可能";
    case "alreadyCurrentlyEnrolled":
      return `${labels.enrollment}中`;
    case "inactive":
      return `${labels.student}無効`;
  }
}

function skippedReasonLabel(reason: string, labels: TenantLabels): string {
  switch (reason) {
    case "alreadyCurrentlyEnrolled":
      return `すでに${labels.enrollment}中`;
    case "studentInactive":
      return `${labels.student}が無効`;
    default:
      return "対象外";
  }
}

function sortRows(rows: BulkStudentRow[], sort: SortKey, order: SortOrder): BulkStudentRow[] {
  const direction = order === "desc" ? -1 : 1;

  return [...rows].sort((left, right) => {
    const leftValue =
      sort === "displayNameKana"
        ? left.displayNameKana || left.displayName
        : sort === "familyAccount"
          ? left.familyAccountDisplayName || ""
          : left[sort];
    const rightValue =
      sort === "displayNameKana"
        ? right.displayNameKana || right.displayName
        : sort === "familyAccount"
          ? right.familyAccountDisplayName || ""
          : right[sort];

    return direction * (leftValue.localeCompare(rightValue) || left.displayName.localeCompare(right.displayName));
  });
}

function sortCurrentEnrollmentRecords(enrollments: EnrollmentDto[]): EnrollmentDto[] {
  return [...enrollments].sort((left, right) => {
    const leftStatusOrder = isCurrentEnrollmentStatus(left.status) ? currentEnrollmentStatusOrder[left.status] : 99;
    const rightStatusOrder = isCurrentEnrollmentStatus(right.status) ? currentEnrollmentStatusOrder[right.status] : 99;

    return (
      leftStatusOrder - rightStatusOrder
      || left.joinDate.localeCompare(right.joinDate)
      || (left.leaveDate ?? "").localeCompare(right.leaveDate ?? "")
      || left.lessonTitle.localeCompare(right.lessonTitle)
    );
  });
}

function sortCurrentEnrollmentRows(rows: CurrentEnrollmentStudentRow[]): CurrentEnrollmentStudentRow[] {
  return [...rows].sort((left, right) =>
    (left.displayNameKana || left.displayName).localeCompare(right.displayNameKana || right.displayName)
    || left.displayName.localeCompare(right.displayName)
    || left.memberCode.localeCompare(right.memberCode)
  );
}

export function AdminBulkEnrollmentCreatePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [lessons, setLessons] = useState<AdminLessonListItemDto[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [form, setForm] = useState<BulkFormState>(defaultFormState);
  const [draftQuery, setDraftQuery] = useState(searchParams.get("q") ?? "");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [candidateExpandedStudentId, setCandidateExpandedStudentId] = useState<string | null>(null);
  const [currentSearchDraft, setCurrentSearchDraft] = useState("");
  const [currentAppliedQuery, setCurrentAppliedQuery] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState<CurrentEnrollmentStatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState<PageSize>(25);
  const [currentEnrollmentExpandedStudentId, setCurrentEnrollmentExpandedStudentId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkCreateEnrollmentsResponseDto | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const selectedLessonId = searchParams.get("lessonId") ?? "";
  const appliedQuery = searchParams.get("q") ?? "";
  const studentStatusFilter = (searchParams.get("studentStatus") as StudentStatusFilter | null) ?? "active";
  const eligibilityFilter = (searchParams.get("eligibility") as EligibilityFilter | null) ?? "eligible";
  const familyAccountFilter = searchParams.get("familyAccountId") ?? "all";
  const sort = (searchParams.get("sort") as SortKey | null) ?? "displayNameKana";
  const order = (searchParams.get("order") as SortOrder | null) ?? "asc";
  const page = parsePositiveInteger(searchParams.get("page"));
  const pageSize = parsePageSize(searchParams.get("pageSize"));

  function updateQuery(next: Record<string, string | null>, resetPage = true) {
    const params = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(next)) {
      if (value === null || value.length === 0 || value === "all") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }

    if (resetPage) {
      params.delete("page");
    }

    setSearchParams(params);
  }

  async function refreshData() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudents, nextLessons, nextEnrollments] = await Promise.all([
        fetchAdminStudents(tenantSlug),
        fetchAdminLessons(tenantSlug),
        fetchAdminEnrollments(tenantSlug)
      ]);

      setStudents(nextStudents);
      setLessons(nextLessons.items);
      setEnrollments(nextEnrollments);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "一括登録に必要な情報の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshData();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  useEffect(() => {
    setDraftQuery(appliedQuery);
  }, [appliedQuery]);

  const selectedLesson = useMemo(
    () => lessons.find((lesson) => lesson.lessonId === selectedLessonId) ?? null,
    [lessons, selectedLessonId]
  );
  const availableLessons = useMemo(
    () => lessons.filter((lesson) => lesson.status !== "ended" && lesson.status !== "archived"),
    [lessons]
  );
  const allRows = useMemo(() => {
    const enrollmentItems = enrollments?.items ?? [];
    return (students?.items ?? []).map((student) => {
      const selectedLessonHistory = selectedLessonId
        ? enrollmentItems.filter((enrollment) =>
            enrollment.studentId === student.studentId && enrollment.lessonId === selectedLessonId
          )
        : [];
      const currentLessons = enrollmentItems
        .filter((enrollment) =>
          enrollment.studentId === student.studentId
          && (enrollment.status === "pendingStart" || enrollment.status === "active" || enrollment.status === "paused")
        )
        .map((enrollment) => enrollment.lessonTitle);

      return {
        studentId: student.studentId,
        memberCode: student.memberCode,
        displayName: student.displayName,
        displayNameKana: student.displayNameKana,
        birthDate: student.birthDate,
        status: student.status,
        familyAccountDisplayName: student.familyAccountDisplayName,
        state: resolveStudentState(student, selectedLessonHistory),
        searchText: buildAdminSearchHaystack([
          student.displayName,
          student.displayNameKana,
          student.memberCode,
          student.familyAccountDisplayName
        ]),
        currentLessons,
        selectedLessonHistory,
        hasPastSelectedLessonHistory: selectedLessonHistory.some((enrollment) =>
          enrollment.status === "ended" || enrollment.status === "cancelled"
        )
      } satisfies BulkStudentRow;
    });
  }, [enrollments, selectedLessonId, students]);

  const currentEnrollmentSummary = useMemo(() => {
    const studentsById = new Map((students?.items ?? []).map((student) => [student.studentId, student]));
    const groupedEnrollments = new Map<string, EnrollmentDto[]>();
    let missingCurrentEnrollmentCount = 0;

    if (!selectedLessonId) {
      return { rows: [] as CurrentEnrollmentStudentRow[], missingCurrentEnrollmentCount };
    }

    for (const enrollment of enrollments?.items ?? []) {
      if (enrollment.lessonId !== selectedLessonId || !isCurrentEnrollmentStatus(enrollment.status)) {
        continue;
      }

      const student = studentsById.get(enrollment.studentId);
      if (!student) {
        missingCurrentEnrollmentCount += 1;
        continue;
      }

      const existing = groupedEnrollments.get(enrollment.studentId) ?? [];
      existing.push(enrollment);
      groupedEnrollments.set(enrollment.studentId, existing);
    }

    const rows = [...groupedEnrollments.entries()].map(([studentId, studentEnrollments]) => {
      const student = studentsById.get(studentId);
      if (!student) {
        throw new Error("Current enrollment student group must have an existing student");
      }

      const currentEnrollments = sortCurrentEnrollmentRecords(studentEnrollments);
      const primaryEnrollment = currentEnrollments[0];
      if (!primaryEnrollment) {
        throw new Error("Current enrollment student row must have at least one enrollment");
      }

      return {
        studentId,
        memberCode: student.memberCode,
        displayName: student.displayName,
        displayNameKana: student.displayNameKana,
        birthDate: student.birthDate,
        status: student.status,
        familyAccountDisplayName: student.familyAccountDisplayName,
        searchText: buildAdminSearchHaystack([
          student.displayName,
          student.displayNameKana,
          student.memberCode,
          student.familyAccountDisplayName
        ]),
        currentEnrollments,
        primaryEnrollment,
        hasDuplicateCurrentEnrollments: currentEnrollments.length > 1,
        hasInactiveStudentWarning: student.status !== "active"
      } satisfies CurrentEnrollmentStudentRow;
    });

    return { rows: sortCurrentEnrollmentRows(rows), missingCurrentEnrollmentCount };
  }, [enrollments, selectedLessonId, students]);

  const familyAccountOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of allRows) {
      if (row.familyAccountDisplayName) names.add(row.familyAccountDisplayName);
    }
    return [...names].sort((left, right) => left.localeCompare(right));
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const byFilters = allRows.filter((row) => {
      if (!adminSearchMatches(appliedQuery, row.searchText)) return false;
      if (studentStatusFilter !== "all" && row.status !== studentStatusFilter) return false;
      if (familyAccountFilter === "__unset" && row.familyAccountDisplayName) return false;
      if (familyAccountFilter !== "all" && familyAccountFilter !== "__unset" && row.familyAccountDisplayName !== familyAccountFilter) return false;
      if (eligibilityFilter === "eligible" && row.state !== "eligible") return false;
      if (eligibilityFilter === "alreadyCurrentlyEnrolled" && row.state !== "alreadyCurrentlyEnrolled") return false;
      if (eligibilityFilter === "pastHistory" && !row.hasPastSelectedLessonHistory) return false;
      return true;
    });

    return sortRows(byFilters, sort, order);
  }, [allRows, appliedQuery, eligibilityFilter, familyAccountFilter, order, sort, studentStatusFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const eligibleFilteredRows = filteredRows.filter((row) => row.state === "eligible");
  const visibleSelectedCount = pageRows.filter((row) => selectedIds.has(row.studentId)).length;
  const hiddenSelectedCount = [...selectedIds].filter((studentId) =>
    !filteredRows.some((row) => row.studentId === studentId)
  ).length;
  const currentFilteredRows = useMemo(() => {
    return currentEnrollmentSummary.rows.filter((row) => {
      if (!adminSearchMatches(currentAppliedQuery, row.searchText)) return false;
      if (currentStatusFilter !== "all" && row.primaryEnrollment.status !== currentStatusFilter) return false;
      return true;
    });
  }, [currentAppliedQuery, currentEnrollmentSummary.rows, currentStatusFilter]);
  const currentTotalPages = Math.max(1, Math.ceil(currentFilteredRows.length / currentPageSize));
  const currentSafePage = Math.min(currentPage, currentTotalPages);
  const currentPageRows = currentFilteredRows.slice(
    (currentSafePage - 1) * currentPageSize,
    currentSafePage * currentPageSize
  );
  const currentWarningCount = currentEnrollmentSummary.rows.filter((row) =>
    row.hasDuplicateCurrentEnrollments || row.hasInactiveStudentWarning
  ).length;

  function resetCurrentEnrollmentUi() {
    setCurrentSearchDraft("");
    setCurrentAppliedQuery("");
    setCurrentStatusFilter("all");
    setCurrentPage(1);
    setCurrentPageSize(25);
    setCurrentEnrollmentExpandedStudentId(null);
  }

  function handleLessonChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextLessonId = event.target.value;

    if (selectedIds.size > 0 && nextLessonId !== selectedLessonId) {
      const confirmed = window.confirm(`${tenantLabels.lesson}を変更すると、選択中の${tenantLabels.student}はクリアされます。変更しますか？`);
      if (!confirmed) {
        return;
      }
    }

    setSelectedIds(new Set());
    setCandidateExpandedStudentId(null);
    if (nextLessonId !== selectedLessonId) {
      resetCurrentEnrollmentUi();
    }
    setConfirming(false);
    setResult(null);
    updateQuery({ lessonId: nextLessonId });
  }

  function addEligibleStudentIds(studentIds: string[]) {
    setFeedbackMessage(null);
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const studentId of studentIds) {
        next.add(studentId);
      }

      if (next.size > 200) {
        setFeedbackMessage("一括登録は最大200名までです。選択数を減らしてください。");
        return current;
      }

      return next;
    });
  }

  function toggleStudent(row: BulkStudentRow) {
    if (row.state !== "eligible") return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(row.studentId)) {
        next.delete(row.studentId);
      } else if (next.size >= 200) {
        setFeedbackMessage("一括登録は最大200名までです。");
        return current;
      } else {
        next.add(row.studentId);
      }
      return next;
    });
  }

  function selectCurrentPage() {
    addEligibleStudentIds(pageRows.filter((row) => row.state === "eligible").map((row) => row.studentId));
  }

  function selectAllFilteredEligible() {
    if (eligibleFilteredRows.length > 200) {
      setFeedbackMessage("絞り込み結果が200名を超えています。条件を絞ってから選択してください。");
      return;
    }

    addEligibleStudentIds(eligibleFilteredRows.map((row) => row.studentId));
  }

  function clearVisibleSelections() {
    const visibleIds = new Set(pageRows.map((row) => row.studentId));
    setSelectedIds((current) => new Set([...current].filter((studentId) => !visibleIds.has(studentId))));
  }

  function applySearch(event?: FormEvent) {
    event?.preventDefault();
    updateQuery({ q: draftQuery.trim() });
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    applySearch();
  }

  function applyCurrentEnrollmentSearch(event?: FormEvent) {
    event?.preventDefault();
    setCurrentAppliedQuery(currentSearchDraft.trim());
    setCurrentPage(1);
    setCurrentEnrollmentExpandedStudentId(null);
  }

  function handleCurrentEnrollmentSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    applyCurrentEnrollmentSearch();
  }

  function handleCurrentEnrollmentStatusFilterChange(event: ChangeEvent<HTMLSelectElement>) {
    setCurrentStatusFilter(event.target.value as CurrentEnrollmentStatusFilter);
    setCurrentPage(1);
    setCurrentEnrollmentExpandedStudentId(null);
  }

  function handleCurrentPageSizeChange(event: ChangeEvent<HTMLSelectElement>) {
    setCurrentPageSize(parsePageSize(event.target.value));
    setCurrentPage(1);
    setCurrentEnrollmentExpandedStudentId(null);
  }

  async function handleSubmitBulk() {
    if (!selectedLesson || selectedIds.size === 0 || submitting) {
      return;
    }

    setSubmitting(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const response = await bulkCreateEnrollments(tenantSlug, {
        lessonId: selectedLesson.lessonId,
        studentIds: [...selectedIds],
        joinDate: form.joinDate || null,
        leaveDate: form.leaveDate || null,
        notesInternal: form.notesInternal.trim() || null
      });

      setResult(response);
      setConfirming(false);
      setCandidateExpandedStudentId(null);
      setCurrentEnrollmentExpandedStudentId(null);
      setSelectedIds((current) => {
        const processed = new Set([
          ...response.created.map((enrollment) => enrollment.studentId),
          ...response.skipped.map((item) => item.studentId)
        ]);
        return new Set([...current].filter((studentId) => !processed.has(studentId)));
      });
      await refreshData();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "一括登録に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      layoutVariant="wide"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.lesson}単位で一括登録`}
      description={`${tenantLabels.lesson}を選び、${tenantLabels.enrollment}する${tenantLabels.student}をまとめて登録します。`}
      links={buildAdminLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>一括登録に必要な情報を読み込んでいます。</p> : null}
        {loadError ? <p role="alert">{loadError}</p> : null}
        {feedbackMessage ? <p role="status">{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>1. {tenantLabels.lesson}を選択</h2>
              <select value={selectedLessonId} onChange={handleLessonChange} aria-label={`一括登録する${tenantLabels.lesson}`}>
                <option value="">{tenantLabels.lesson}を選択してください</option>
                {availableLessons.map((lesson) => (
                  <option key={lesson.lessonId} value={lesson.lessonId}>
                    {lesson.title} / {lesson.lessonCode} / {describeStatus(lesson.status)}
                  </option>
                ))}
              </select>
              {selectedLesson ? (
                <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
                  <strong>{selectedLesson.title}</strong>
                  <span>コード: {selectedLesson.lessonCode}</span>
                  <span>種別: {describeStatus(selectedLesson.lessonKind)} / 状態: {describeStatus(selectedLesson.status)}</span>
                  <span>開始日: {selectedLesson.startDate}</span>
                  <span>{tenantLabels.studio}: {selectedLesson.defaultStudioName ?? "未設定"}</span>
                  <span>担当{tenantLabels.teacher}: {selectedLesson.primaryTeacherNameSnapshots.join("、") || "未設定"}</span>
                </div>
              ) : null}
            </section>

            <section style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>2. 登録内容</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <span>{tenantLabels.enrollment}種別: {describeStatus("regular")}</span>
                <span>ステータス: {describeStatus("auto")}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
                未入力の場合、{tenantLabels.lesson}開始日または登録日から自動設定します。開始日が未来の場合は開始待ち、それ以外は有効になります。
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>開始日（任意）</span>
                  <input type="date" value={form.joinDate} onChange={(event) => setForm((current) => ({ ...current, joinDate: event.target.value }))} />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>終了日（任意）</span>
                  <input type="date" value={form.leaveDate} onChange={(event) => setForm((current) => ({ ...current, leaveDate: event.target.value }))} />
                </label>
              </div>
              <label style={{ display: "grid", gap: 4 }}>
                <span>内部メモ（任意）</span>
                <textarea rows={2} value={form.notesInternal} onChange={(event) => setForm((current) => ({ ...current, notesInternal: event.target.value }))} />
              </label>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>3. {tenantLabels.student}を選択</h2>
              <form onSubmit={applySearch} style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8 }}>
                  <input
                    value={draftQuery}
                    onChange={(event) => setDraftQuery(event.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={`${tenantLabels.student}名・かな・会員コード・${tenantLabels.familyAccount}名で検索`}
                    aria-label={`${tenantLabels.student}検索`}
                  />
                  <button type="submit">検索</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <select value={studentStatusFilter} onChange={(event) => updateQuery({ studentStatus: event.target.value })} aria-label={`${tenantLabels.student}ステータス`}>
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                    <option value="all">すべて</option>
                  </select>
                  <select value={eligibilityFilter} onChange={(event) => updateQuery({ eligibility: event.target.value })} aria-label="登録可否">
                    <option value="eligible">登録可能</option>
                    <option value="all">すべて</option>
                    <option value="alreadyCurrentlyEnrolled">現在{tenantLabels.enrollment}中</option>
                    <option value="pastHistory">過去{tenantLabels.enrollment}あり</option>
                  </select>
                  <select value={familyAccountFilter} onChange={(event) => updateQuery({ familyAccountId: event.target.value })} aria-label={tenantLabels.familyAccount}>
                    <option value="all">{tenantLabels.familyAccount}: すべて</option>
                    <option value="__unset">未設定</option>
                    {familyAccountOptions.map((familyAccountName) => (
                      <option key={familyAccountName} value={familyAccountName}>{familyAccountName}</option>
                    ))}
                  </select>
                  <select value={sort} onChange={(event) => updateQuery({ sort: event.target.value })} aria-label="並び替え">
                    <option value="displayNameKana">表示名かな</option>
                    <option value="displayName">{tenantLabels.student}名</option>
                    <option value="memberCode">会員コード</option>
                    <option value="familyAccount">{tenantLabels.familyAccount}名</option>
                  </select>
                  <select value={order} onChange={(event) => updateQuery({ order: event.target.value })} aria-label="並び順">
                    <option value="asc">昇順</option>
                    <option value="desc">降順</option>
                  </select>
                  <select value={String(pageSize)} onChange={(event) => updateQuery({ pageSize: event.target.value })} aria-label="表示件数">
                    <option value="25">25件</option>
                    <option value="50">50件</option>
                    <option value="100">100件</option>
                  </select>
                </div>
              </form>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span>選択中: {selectedIds.size}名</span>
                {hiddenSelectedCount > 0 ? <span>{hiddenSelectedCount}名が現在の絞り込みでは非表示です。</span> : null}
                <button type="button" onClick={selectCurrentPage} disabled={!selectedLesson}>現在ページの登録可能な{tenantLabels.student}を選択</button>
                <button type="button" onClick={selectAllFilteredEligible} disabled={!selectedLesson}>絞り込み結果の登録可能な{tenantLabels.student}をすべて選択</button>
                <button type="button" onClick={clearVisibleSelections}>表示中の選択を解除</button>
                <button type="button" onClick={() => setSelectedIds(new Set())}>すべて解除</button>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                {pageRows.map((row) => {
                  const detailId = `candidate-student-details-${row.studentId}`;
                  const expanded = candidateExpandedStudentId === row.studentId;
                  const checked = selectedIds.has(row.studentId);
                  return (
                    <div key={row.studentId} style={{ border: "1px solid #e5e5e5", borderRadius: 10, overflow: "hidden" }}>
                      <div style={{ minHeight: 46, display: "grid", gridTemplateColumns: "44px minmax(0, 1fr) auto 44px", gap: 8, alignItems: "center", padding: "0 8px" }}>
                        <input
                          type="checkbox"
                          aria-label={`${row.displayName}を選択`}
                          checked={checked}
                          disabled={row.state !== "eligible"}
                          onChange={() => toggleStudent(row)}
                        />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700 }}>{row.displayName}</span>
                        <span style={{ fontSize: 12, padding: "3px 8px", borderRadius: 999, background: row.state === "eligible" ? "#eefaf0" : "#f3f3f3", whiteSpace: "nowrap" }}>
                          {stateBadgeLabel(row.state, tenantLabels)}
                        </span>
                        <button
                          type="button"
                          aria-label={`${row.displayName}の詳細を${expanded ? "閉じる" : "開く"}`}
                          aria-expanded={expanded}
                          aria-controls={detailId}
                          onClick={() => setCandidateExpandedStudentId(expanded ? null : row.studentId)}
                          style={{ minHeight: 44, minWidth: 44 }}
                        >
                          {expanded ? "∧" : "∨"}
                        </button>
                      </div>
                      {expanded ? (
                        <div id={detailId} style={{ display: "grid", gap: 6, padding: 12, background: "#fafafa", fontSize: 13 }}>
                          <span>会員コード: {row.memberCode}</span>
                          <span>表示名かな: {row.displayNameKana ?? "未設定"}</span>
                          <span>生年月日: {row.birthDate}</span>
                          <span>{tenantLabels.familyAccount}: {row.familyAccountDisplayName ?? "未設定"}</span>
                          <span>{tenantLabels.student}ステータス: {describeStatus(row.status)}</span>
                          <span>現在{tenantLabels.enrollment}中の{tenantLabels.lesson}: {row.currentLessons.join("、") || "なし"}</span>
                          <div>
                            <strong>選択{tenantLabels.lesson}の{tenantLabels.enrollment}履歴</strong>
                            {row.selectedLessonHistory.length === 0 ? (
                              <p style={{ margin: "4px 0 0" }}>この{tenantLabels.lesson}への{tenantLabels.enrollment}履歴はありません。</p>
                            ) : (
                              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                                {row.selectedLessonHistory.map((enrollment) => (
                                  <li key={enrollment.enrollmentId}>
                                    {enrollment.status === "ended" || enrollment.status === "cancelled" ? `過去の${tenantLabels.enrollment}あり` : `現在${tenantLabels.enrollment}中`}:
                                    {" "}{describeStatus(enrollment.status)} / 開始日 {enrollment.joinDate} / 終了日 {enrollment.leaveDate ?? "なし"}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <button type="button" disabled={safePage <= 1} onClick={() => updateQuery({ page: String(safePage - 1) }, false)}>前へ</button>
                <span>{safePage} / {totalPages} ページ（{filteredRows.length}件）</span>
                <button type="button" disabled={safePage >= totalPages} onClick={() => updateQuery({ page: String(safePage + 1) }, false)}>次へ</button>
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>4. {tenantLabels.enrollment}中{tenantLabels.student}</h2>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
                選択中の{tenantLabels.lesson}に現在{tenantLabels.enrollment}中の{tenantLabels.student}を確認できます。この一覧は読み取り専用です。
              </p>
              {selectedLesson ? (
                <>
                  <form onSubmit={applyCurrentEnrollmentSearch} style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8 }}>
                      <input
                        value={currentSearchDraft}
                        onChange={(event) => setCurrentSearchDraft(event.target.value)}
                        onKeyDown={handleCurrentEnrollmentSearchKeyDown}
                        placeholder={`${tenantLabels.student}名・かな・会員コード・${tenantLabels.familyAccount}名で検索`}
                        aria-label={`${tenantLabels.enrollment}中${tenantLabels.student}検索`}
                      />
                      <button type="submit">検索</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <select value={currentStatusFilter} onChange={handleCurrentEnrollmentStatusFilterChange} aria-label={`${tenantLabels.enrollment}ステータス`}>
                        <option value="all">現在{tenantLabels.enrollment}中: すべて</option>
                        <option value="pendingStart">{describeStatus("pendingStart")}</option>
                        <option value="active">{describeStatus("active")}</option>
                        <option value="paused">{describeStatus("paused")}</option>
                      </select>
                      <select value={String(currentPageSize)} onChange={handleCurrentPageSizeChange} aria-label={`${tenantLabels.enrollment}中${tenantLabels.student}表示件数`}>
                        <option value="25">25件</option>
                        <option value="50">50件</option>
                        <option value="100">100件</option>
                      </select>
                    </div>
                  </form>

                  <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                    <span>
                      現在{tenantLabels.enrollment}中: {currentEnrollmentSummary.rows.length}名
                      {currentAppliedQuery || currentStatusFilter !== "all" ? ` / 表示: ${currentFilteredRows.length}名` : ""}
                    </span>
                    {currentEnrollmentSummary.missingCurrentEnrollmentCount > 0 ? (
                      <span role="alert" style={{ color: "#8a5a00" }}>
                        {tenantLabels.student}情報を確認できない{tenantLabels.enrollment}が{currentEnrollmentSummary.missingCurrentEnrollmentCount}件あります。
                      </span>
                    ) : null}
                    {currentWarningCount > 0 ? (
                      <span style={{ color: "#8a5a00" }}>要確認の{tenantLabels.enrollment}データが{currentWarningCount}件あります。</span>
                    ) : null}
                  </div>

                  {loadState === "loading" ? <p>現在の{tenantLabels.enrollment}状況を読み込んでいます。</p> : null}
                  {loadState !== "loading" && currentEnrollmentSummary.rows.length === 0 ? (
                    <p>この{tenantLabels.lesson}には現在{tenantLabels.enrollment}中の{tenantLabels.student}はいません。</p>
                  ) : null}
                  {loadState !== "loading" && currentEnrollmentSummary.rows.length > 0 && currentFilteredRows.length === 0 ? (
                    <p>条件に一致する現在{tenantLabels.enrollment}中の{tenantLabels.student}はいません。</p>
                  ) : null}

                  <div style={{ display: "grid", gap: 6 }}>
                    {currentPageRows.map((row) => {
                      const detailId = `current-enrollment-student-details-${row.studentId}`;
                      const expanded = currentEnrollmentExpandedStudentId === row.studentId;

                      return (
                        <div key={row.studentId} style={{ border: "1px solid #e5e5e5", borderRadius: 10, overflow: "hidden" }}>
                          <div style={{ minHeight: 46, display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto 44px", gap: 8, alignItems: "center", padding: "0 8px" }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700 }}>{row.displayName}</span>
                            <span style={{ fontSize: 12, padding: "3px 8px", borderRadius: 999, background: "#eef5ff", whiteSpace: "nowrap" }}>
                              {describeStatus(row.primaryEnrollment.status)}
                            </span>
                            <span style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                              {row.hasInactiveStudentWarning ? (
                                <span style={{ fontSize: 12, padding: "3px 8px", borderRadius: 999, background: "#fff4d6", whiteSpace: "nowrap" }}>
                                  {tenantLabels.student}無効・要確認
                                </span>
                              ) : null}
                              {row.hasDuplicateCurrentEnrollments ? (
                                <span style={{ fontSize: 12, padding: "3px 8px", borderRadius: 999, background: "#fff4d6", whiteSpace: "nowrap" }}>
                                  {tenantLabels.enrollment}データ要確認
                                </span>
                              ) : null}
                            </span>
                            <button
                              type="button"
                              aria-label={`${row.displayName}の現在の${tenantLabels.enrollment}詳細を${expanded ? "閉じる" : "開く"}`}
                              aria-expanded={expanded}
                              aria-controls={detailId}
                              onClick={() => setCurrentEnrollmentExpandedStudentId(expanded ? null : row.studentId)}
                              style={{ minHeight: 44, minWidth: 44 }}
                            >
                              {expanded ? "∧" : "∨"}
                            </button>
                          </div>
                          {expanded ? (
                            <div id={detailId} style={{ display: "grid", gap: 6, padding: 12, background: "#fafafa", fontSize: 13 }}>
                              <span>会員コード: {row.memberCode}</span>
                              <span>表示名かな: {row.displayNameKana ?? "未設定"}</span>
                              <span>生年月日: {row.birthDate}</span>
                              <span>{tenantLabels.familyAccount}: {row.familyAccountDisplayName ?? "未設定"}</span>
                              <span>{tenantLabels.student}ステータス: {describeStatus(row.status)}</span>
                              <div style={{ display: "grid", gap: 6 }}>
                                <strong>現在の{tenantLabels.enrollment}データ</strong>
                                {row.currentEnrollments.length > 1 ? (
                                  <span style={{ color: "#8a5a00" }}>現在の{tenantLabels.enrollment}データが{row.currentEnrollments.length}件あります。</span>
                                ) : null}
                                <ul style={{ display: "grid", gap: 8, margin: 0, paddingLeft: 18 }}>
                                  {row.currentEnrollments.map((enrollment, index) => (
                                    <li key={enrollment.enrollmentId}>
                                      <div style={{ display: "grid", gap: 3 }}>
                                        <strong>{row.currentEnrollments.length > 1 ? `${index + 1}件目` : `${tenantLabels.enrollment}データ`}</strong>
                                        <span>{tenantLabels.enrollment}種別: {describeStatus(enrollment.enrollmentType)}</span>
                                        <span>{tenantLabels.enrollment}ステータス: {describeStatus(enrollment.status)}</span>
                                        <span>{tenantLabels.enrollment}開始日: {enrollment.joinDate}</span>
                                        <span>{tenantLabels.enrollment}終了日: {enrollment.leaveDate ?? "終了日未設定"}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <button type="button" disabled={currentSafePage <= 1} onClick={() => setCurrentPage(currentSafePage - 1)}>前へ</button>
                    <span>{currentSafePage} / {currentTotalPages} ページ（{currentFilteredRows.length}件）</span>
                    <button type="button" disabled={currentSafePage >= currentTotalPages} onClick={() => setCurrentPage(currentSafePage + 1)}>次へ</button>
                  </div>
                </>
              ) : (
                <p>{tenantLabels.lesson}を選択すると、現在{tenantLabels.enrollment}中の{tenantLabels.student}を確認できます。</p>
              )}
            </section>

            <section style={{ position: "sticky", bottom: "calc(var(--protected-bottom-nav-content-offset, 76px) - 12px)", display: "grid", gap: 8, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, background: "#fff", boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                <strong>一括登録: {selectedIds.size}名選択中</strong>
              <button type="button" disabled={!selectedLesson || selectedIds.size === 0 || submitting} onClick={() => setConfirming(true)}>
                登録内容を確認
              </button>
            </section>

            {confirming ? (
              <section style={{ display: "grid", gap: 10, padding: 12, border: "2px solid #111", borderRadius: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>登録内容を確認</h2>
                <span>{tenantLabels.lesson}: {selectedLesson?.title ?? "未選択"}</span>
                <span>選択した{tenantLabels.student}: {selectedIds.size}名</span>
                <span>{tenantLabels.enrollment}種別: {describeStatus("regular")}</span>
                <span>ステータス: {describeStatus("auto")}</span>
                <span>開始日: {form.joinDate || "未入力のため自動設定"}</span>
                <span>終了日: {form.leaveDate || "設定なし"}</span>
                <p style={{ margin: 0 }}>開始日が未入力の場合、バックエンドが{tenantLabels.lesson}開始日またはテナント timezone の登録日から実際の開始日を決定します。</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" disabled={submitting} onClick={() => void handleSubmitBulk()}>
                    {submitting ? "登録中..." : `${selectedIds.size}名を一括登録`}
                  </button>
                  <button type="button" disabled={submitting} onClick={() => setConfirming(false)}>戻る</button>
                </div>
              </section>
            ) : null}

            {result ? (
              <section style={{ display: "grid", gap: 10, padding: 12, border: "1px solid #d7efd8", borderRadius: 12, background: "#f8fff8" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>登録結果</h2>
                <p style={{ margin: 0 }}>
                  {result.createdCount > 0
                    ? `${result.createdCount}名を登録しました。`
                    : `新しく登録された${tenantLabels.enrollment}はありませんでした。`}
                  {result.skippedCount > 0 ? ` ${result.skippedCount}名は対象外でした。` : ""}
                </p>
                <span>実際の開始日: {result.effectiveJoinDate}</span>
                <span>登録ステータス: {describeStatus(result.created[0]?.status ?? "auto")}</span>
                <span>作成件数: {result.createdCount}</span>
                <span>対象外件数: {result.skippedCount}</span>
                {result.skipped.length > 0 ? (
                  <details>
                    <summary>対象外の{tenantLabels.student}を表示</summary>
                    <ul>
                      {result.skipped.map((item) => (
                        <li key={item.studentId}>{item.studentDisplayName} / 理由: {skippedReasonLabel(item.reason, tenantLabels)}</li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                <Link to={`/t/${tenantSlug}/teacher/admin/enrollments`}>{tenantLabels.enrollment}一覧へ</Link>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
